"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LIVE_FRAMES = exports.changeEventSchema = exports.LIVE_TOPICS = exports.REQUEST_CHANNELS = exports.WORKSPACE_WIDE_ACTIONS = exports.AUDIT_ACTIONS = void 0;
exports.topicOf = topicOf;
const zod_1 = require("zod");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C21 — what changed, as a POINTER. Never as a row.                         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `decisions.md` R-19. The API holds an SSE connection open per tab and writes one of these
 * whenever anything in the caller's workspace changes. The client's answer is always the same:
 * refetch what depends on it.
 *
 * ⚠️⚠️ **THERE IS NO ROW IN HERE, AND THERE MUST NOT BE.** Three reasons, and they are
 * independent:
 *
 * 1. **Permissions.** A read is trimmed twice — RLS, and `CheckService` applying scope and field
 *    rules *in serialization*, where a hidden field is dropped rather than blanked. A row on this
 *    channel has been through neither, so pushing one means reimplementing the whole check layer
 *    on the fan-out path. An invalidation says only *"something you can see may have changed"*,
 *    and the refetch that follows goes down the ordinary read path and is trimmed as it always is.
 * 2. **Derived reads.** One worklog write changes the project dashboard's logged hours, the
 *    sidebar counts, the task's tracked hours, the timesheet and four reports. **There is no row
 *    to push. There is a set of reads to redo.**
 * 3. **Ordering.** A pointer is idempotent and order-independent; two of them arriving out of
 *    order still produce one correct refetch. A row is neither, and a missed one leaves the screen
 *    confidently wrong.
 *
 * It also keeps the payload far inside `pg_notify`'s 8000-byte limit, which is the mechanism
 * carrying it between API instances.
 */
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ Every audited action, as a closed set.                                 │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * CLAUDE.md rule 4 already requires every mutation to be audited in the same transaction as the
 * change, so `audit_log` is the complete record of what happens in a workspace. C21 makes it the
 * event feed too — one `pg_notify` beside the insert — which means **this list and the change feed
 * are the same list**, and neither can grow without the other.
 *
 * ⚠️ **Typed, rather than `string`.** `recordAudit` used to take any string, checked only by the
 * table's `^[a-z_]+\.[a-z_]+$` constraint — so a typo was a runtime failure inside a transaction,
 * and now would also be an event nothing listens for. `apps/api`'s audit test asserts every literal
 * in the service layer appears here, so this cannot be bypassed by writing a string anyway.
 */
exports.AUDIT_ACTIONS = [
    'account.export',
    /* 0202 · the first-run setup flow's last step. */
    'account.setup',
    'billing.details_update',
    'client.archive',
    'client.create',
    'client.restore',
    'client.update',
    /* C56 · a contract moving on or off a project. Its own verbs, because neither is an
       ordinary field change: attaching mints a code and detaching is GUARDED. */
    'contract.attach',
    'contract.create',
    'contract.detach',
    'contract.update',
    'data.export',
    /* C32 · the marketplace. Its own prefix and topic — a settings screen nothing else re-reads. */
    'extension.install',
    'extension.uninstall',
    /* C33 · one instance's whole life. `integration.sync` is the run, which is the event somebody is
       actually watching for — a colleague's manual sync has to reach an open run list. */
    'integration.create',
    'integration.update',
    'integration.delete',
    'integration.pause',
    'integration.resume',
    'integration.sync',
    /* C34 · what the admin credential can see, and who the people are. Audited because it WRITES —
       staged projects, identities and pairings — and rule 4 does not care that a machine decided. */
    'integration.discover',
    /* C34 · a person turning a discovered container into a link, and taking it back. ⚠️ Two actions
       rather than one `integration.link.set`, because the audit diff for *"stopped syncing this
       project"* is the one somebody goes looking for. */
    'integration.link',
    'integration.unlink',
    /* C42 · a second workspace in a tenant. */
    'workspace.create',
    'data.import',
    'file.attach',
    'file.detach',
    /* C42 · the workspace calendar. `import` changes a SET rather than a row, which is why the
       event's `resourceId` is the workspace and not a day. */
    /* C46 · the leave vocabulary. `leave.quota` covers the grid; the requests C47 adds will be
       `leave.request*`, which is why this one is not simply `leave.update`. */
    /* C47 · the requests. `leave.decide` covers approve AND reject: both are one act with two
       directions, and a screen subscribing to one would miss the other. */
    'leave.request',
    'leave.decide',
    'leave.revoke',
    /* P3-9 · a comment on a request. It moves the same screens a decision does. */
    'leave.comment',
    'leave.create',
    'leave.delete',
    'leave.managed',
    'leave.quota',
    /* P3 · the settings screen's drag handle. The order is the one the request form offers, so it
       is a workspace decision like any other and gets its own row rather than riding on an update. */
    'leave.reorder',
    'leave.update',
    'holiday.create',
    'holiday.delete',
    'holiday.import',
    'holiday.update',
    /* C43 · work schedules. `schedule.assign` REPLACES a person's row for a date rather than adding
       one beside it, which is why there is no separate `unassign`. */
    'schedule.assign',
    'schedule.create',
    'schedule.delete',
    'schedule.unassign',
    'schedule.update',
    /* C51 · the currencies a workspace works in. ⚠️ `currency.default` is its own action rather than
       an update, because moving the default RE-BASES every other rate — one press changes every
       figure in the product, which is not what `update` means anywhere else. */
    'currency.create',
    'currency.default',
    'currency.delete',
    'currency.sync',
    /* FX-5 · one day's rate set by hand, or restored to the bank's. */
    'currency.rate_set',
    'currency.update',
    /* C51 · rate cards. Templates and the assignments that point at them, the same pair as C43's
       schedules — and `ratecard.assign` REPLACES a person's row for a date, for the same reason. */
    'ratecard.assign',
    'ratecard.create',
    'ratecard.delete',
    'ratecard.unassign',
    'ratecard.update',
    /* C52 · the resource plan. ⚠️ One action per OUTCOME, not per endpoint: a save that clears the
       last day of an allocation IS a delete, and the feed has to say so, or a grid watching for
       removals would keep drawing a row the server no longer has. */
    /* C58 · one entry per SCAN rather than per finding: a rescan fires on every edit, and a
       hundred rows for one automatic pass would bury the actions a human actually took. */
    'alert.scan',
    'alert.dismiss',
    'alert.restore',
    'allocation.create',
    'allocation.delete',
    'allocation.update',
    /* C56 · external costs. ⚠️ Their own topic rather than `project`: a licence renewed changes a
       project's margin and nothing else on any project screen, and the finance surfaces are the only
       ones that read them. */
    'extcost.create',
    'extcost.delete',
    'extcost.update',
    /* C57 · invoices. ⚠️ `update` covers a status move too — *Kiállítva* → *Kifizetve* is an
       ordinary edit of the row, and a separate `invoice.settle` would be a verb this product
       does not have (rule 8). */
    'invoice.create',
    'invoice.delete',
    'invoice.update',
    /* C48 · overtime. `raise` rather than `create`, because a claim is raised FROM a worklog and
       never filed on its own — the verb is the design. */
    'overtime.create',
    'overtime.decide',
    'overtime.delete',
    'overtime.raise',
    /* P3 · the settings screen's drag handle — the marking control offers the types in this order. */
    'overtime.reorder',
    /* Phase 3 of the notification emails · the claimant withdrawing a claim still pending. */
    'overtime.revoke',
    'overtime.update',
    /* C54 · skills. Three vocabularies and the join, so the prefix carries a second
       segment naming WHICH list moved — `skill.level_reorder` re-weights the scale, and
       `skill.reorder` only re-sorts a list. They are not the same event to a reader. */
    'skill.category_create',
    'skill.category_delete',
    'skill.category_reorder',
    'skill.category_update',
    'skill.create',
    'skill.delete',
    'skill.level_create',
    'skill.level_delete',
    /* ⚠️⚠️ **NOT COSMETIC.** The order of the levels IS the scale — moving one
       re-weights every person's skill list, so the entry records the whole permutation. */
    'skill.level_reorder',
    'skill.level_update',
    'skill.member_add',
    'skill.member_remove',
    /* ⚠️ The onboarding wizard writes the WHOLE set in one go, so it is its own action
       rather than N adds and M removes: one act by one person, audited as one. */
    'skill.member_set',
    'skill.member_update',
    'skill.reorder',
    'skill.update',
    'member.activate',
    'member.deactivate',
    'member.delete',
    'member.invite',
    'member.invite_resend',
    'member.invite_revoke',
    /* Phase 5 of the notification emails · the 3-day reminder, which mints a second link. */
    'member.invite_remind',
    'member.onboarded',
    'member.roles_set',
    'member.update',
    'lineitem.create',
    'lineitem.delete',
    'lineitem.update',
    'metafield.create',
    'metafield.delete',
    'metafield.duplicate',
    'metafield.reorder',
    'metafield.set_values',
    'metafield.update',
    /* C31 · the project-role vocabulary. Its own prefix so `topicOf` reaches a topic nothing else
       subscribes to — a picker re-reading on every project edit would be noise. */
    'projectrole.create',
    'projectrole.delete',
    'projectrole.reorder',
    'projectrole.update',
    /* R-60 · the team vocabulary. Who is in a team is the PERSON's edit (`member.update`). */
    'team.create',
    'team.delete',
    'team.update',
    'project.create',
    'project.member_add',
    'project.member_remove',
    'project.member_update',
    'project.update',
    'report.create',
    'report.delete',
    'report.duplicate',
    'report.refresh',
    'report.update',
    /* P3-fixes · a workspace can define its own levels now, not only edit the five it starts with. */
    'role.create',
    'role.delete',
    'role.update',
    'task.comment',
    'task.comment_delete',
    'task.comment_edit',
    'task.create',
    'task.delete',
    'task.depend',
    'task.link',
    'task.undepend',
    'task.unlink',
    'task.update',
    'task_priority.create',
    'task_priority.delete',
    'task_priority.reorder',
    'task_priority.set_default',
    'task_priority.update',
    'task_status.create',
    'task_status.delete',
    'task_status.reorder',
    'task_status.set_default',
    'task_status.update',
    /* EV-2 · the workspace's status-category weights. */
    'task_status.weights',
    'token.create',
    'token.revoke',
    'user.notifications',
    /* F-1 · a welcome seen, a tour finished or skipped. */
    'user.onboarding',
    'user.preferences',
    'user.update',
    'worklog.create',
    'worklog.delete',
    'worklog.rules_update',
    'worklog.update',
    'workspace.deleted',
    'workspace.deletion_cancelled',
    'workspace.deletion_requested',
    /* F-3 · the first-run demo, written as one row for the whole seed — and F-5's removal of it. */
    'workspace.demo_purge',
    'workspace.demo_seed',
    'workspace.update',
    /* 20 Oct 2026 · Phase 6 · deal management and client contacts. */
    'contact.create',
    'contact.delete',
    'contact.update',
    'deal.allocations_set',
    'deal.cost_create',
    'deal.cost_delete',
    'deal.cost_update',
    'deal.create',
    'deal.delete',
    'deal.event_create',
    'deal.event_delete',
    'deal.event_update',
    'deal.generate',
    'deal.lineitem_create',
    'deal.lineitem_delete',
    'deal.lineitem_update',
    'deal.lost',
    'deal.milestone_create',
    'deal.milestone_delete',
    'deal.milestone_update',
    'deal.next_step',
    'deal.placeholder_create',
    'deal.placeholder_delete',
    'deal.placeholder_update',
    'deal.probability_create',
    'deal.probability_delete',
    'deal.probability_update',
    'deal.profile_create',
    'deal.profile_delete',
    'deal.profile_update',
    'deal.reason_create',
    'deal.reason_delete',
    'deal.reason_update',
    'deal.reopen',
    'deal.scenario_create',
    'deal.scenario_delete',
    'deal.scenario_update',
    'deal.scenario_winner',
    'deal.settings_update',
    'deal.status',
    'deal.status_create',
    'deal.status_delete',
    'deal.status_reorder',
    'deal.status_update',
    'deal.update',
    'deal.won',
];
/**
 * ⚠️⚠️ **THE ACTIONS THAT CHANGE A WHOLE WORKSPACE AT ONCE, AND REACH EVERY SCREEN AS A RESYNC.**
 * The demo seed writes clients, projects, tasks, hours and people in one transaction and audits it
 * as one row (`decisions.md` F-3). Its prefix is `workspace`, which no task list or timesheet
 * subscribes to — so as a plain change it would land on nobody, and somebody looking at an empty
 * Home while the seed finished would keep looking at an empty Home. The bus turns these into
 * *"refetch everything you hold"*, which is exactly what a reconnect already means.
 */
exports.WORKSPACE_WIDE_ACTIONS = [
    'workspace.demo_seed',
    'workspace.demo_purge',
];
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ C51 · through WHICH DOOR a mutation arrived. `0123`                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `audit_log` has recorded who, what and when since `0006`. It has never recorded *through what*,
 * and until C51 there was one answer. `0120` made a personal access token a credential every
 * endpoint accepts and C51.2 put a second program-facing surface on the same services, so a worklog
 * created this morning may have come from the app, a script, the Agent or somebody's chat client.
 *
 * ⚠️⚠️ **THE FOUR ARE INDISTINGUISHABLE EVERYWHERE ELSE *BY DESIGN*, WHICH IS EXACTLY WHY THIS
 * COLUMN IS WORTH THE MIGRATION.** Rule 1 puts the logic in one service; R-24 gives the tools no
 * authority of their own; R-41's extraction was required to carry no behaviour change. There is no
 * second code path whose presence could be inferred from the data — so if the request does not say,
 * nothing can.
 *
 * ⚠️ **It is evidence, never authorization.** No policy reads it, and no check branches on it: the
 * same sentence `0120` writes about token scopes, for the same reason.
 *
 * ⚠️ **`agent` and `mcp` are decided by where the PROPOSAL came from, not by the accept request.**
 * R-25's gate means the write is always a person clicking in Forecastify, so the accept request is
 * a browser session every time and channelling it by its own transport would record `ui` for both
 * and answer nothing. The conversation's `origin` (`0122`) is the fact that matters.
 *
 * ⚠️ **No `job` and no `integration`, though both are real.** An enum value nothing writes reads as
 * *"we support that"*; `alter type … add value` is one line when a caller for it exists.
 */
exports.REQUEST_CHANNELS = ['ui', 'api', 'agent', 'mcp'];
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ A TOPIC IS AN ACTION'S PREFIX — not its `resource_type`.               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * A screen subscribes to topics; the client's dependency map is keyed by them.
 *
 * ⚠️⚠️ **`resource_type` LOOKS like the right key and is not.** The two diverge in seven real
 * cases and one of them is dynamic:
 *
 * | action                  | `resource_type`                             |
 * | ----------------------- | ------------------------------------------- |
 * | `account.export`        | `export`                                    |
 * | `account.setup`         | `user`                                      |
 * | `data.import`           | `import`                                    |
 * | `member.invite_resend`  | `invitation`                                |
 * | `project.member_update` | `project_member`                            |
 * | `billing.details_update`| `billing_details`                           |
 * | `metafield.set_values`  | **the entity** — task, project, client, …   |
 *
 * The action is the stable half: the column has a `^[a-z_]+\.[a-z_]+$` constraint and the prefix
 * names the thing a reader thinks changed. `resourceType` still travels on the event, because it
 * is exactly what the last row needs — setting metafields on a task should reach a task screen,
 * and the prefix alone says only `metafield`.
 */
exports.LIVE_TOPICS = [
    'account',
    'billing',
    'client',
    'contract',
    'data',
    'extension',
    'file',
    /* C42 · the workspace calendar. A day added or re-kinded changes every daily plan that reads
       it, so the settings screen is live and so is anything the day kernel feeds. */
    'holiday',
    /* C46 · leave types, quotas, and C47's requests. */
    'leave',
    /* C43 · work schedule templates and the assignments that point at them. */
    'schedule',
    /* C51 · the money vocabulary. A rate moving re-prices every figure that reads it, so both are
       live — a cost figure computed against yesterday's rate is a wrong number, not a stale one. */
    'currency',
    'ratecard',
    /* C52 · the resource plan. One person's hours move three screens at once — the Erőforrás grid,
       the project's resource tab and the finance dashboard — so a cell typed on any of them reaches
       the other two. */
    /* C58 · findings and the exceptions that suppress them — one topic, because no screen
       reads one without the other. */
    'alert',
    'allocation',
    /* C56 · what a project spends that is not time. */
    'extcost',
    /* C57 · what has been billed. One invoice moves three surfaces — the workspace list, the
       project tab, and *Kiszámlázva* on whichever line item or period it was billed against. */
    'invoice',
    'integration',
    'lineitem',
    'member',
    'metafield',
    /* C48 · overtime types and the claims raised against them. */
    'overtime',
    /* C54 · the skill vocabulary and who holds what in it. **One topic for all four
       tables**, because a category renamed changes the dot on every skill row, a level
       reordered re-sorts every person's list, and the four surfaces that draw any of it draw
       all of it. Splitting them would be four subscriptions where nothing reads one without
       the others. */
    'skill',
    'project',
    'projectrole',
    'report',
    'role',
    'task',
    'task_priority',
    'task_status',
    /* R-60 · the team vocabulary; every screen that names or filters by a team reads it. */
    'team',
    'token',
    'user',
    'worklog',
    'workspace',
    /* 20 Oct 2026 · Phase 6 · one topic for a deal and everything hung off it — its settings too,
       since a status renamed changes every board column and every row's badge. */
    'deal',
    'contact',
];
/**
 * The topic an action belongs to.
 *
 * ⚠️ Total by construction — `live.test.ts` asserts every action's prefix is a member of
 * `LIVE_TOPICS`, in both directions, so this cannot return something nobody subscribes to.
 */
function topicOf(action) {
    return action.slice(0, action.indexOf('.'));
}
/**
 * One change, as it crosses `pg_notify` and then the SSE stream.
 *
 * ⚠️ **The same shape on both hops.** The API does not re-wrap it: what Postgres carries between
 * instances is what the browser receives, so there is one schema to keep right rather than two
 * that have to agree.
 */
exports.changeEventSchema = zod_1.z.object({
    /** Which workspace. The stream filters on it; the browser never sees another's. */
    workspaceId: zod_1.z.uuid(),
    action: zod_1.z.enum(exports.AUDIT_ACTIONS),
    /** Diverges from the action's prefix — see `LIVE_TOPICS`. Free-form because of `set_values`. */
    resourceType: zod_1.z.string(),
    /** `null` for the actions that change a set rather than a row — a reorder, an import. */
    resourceId: zod_1.z.uuid().nullable(),
    /**
     * ⚠️ **Who did it and which request, so a tab can recognise the echo of its own write.**
     * Without these every change round-trips twice: once optimistically, once because the server
     * told you about the thing you just did.
     */
    actorMemberId: zod_1.z.uuid().nullable(),
    requestId: zod_1.z.uuid(),
});
/**
 * The frames the stream can carry.
 *
 * ⚠️⚠️ **`resync` IS NOT A REPLAY, AND THAT IS SETTLED** (`hard-won.md` P1-T18-3). A reconnected
 * stream has no idea what it missed and `pg_notify` keeps no history; giving it one means a durable
 * per-member queue beside the inbox that already exists. So a reconnect says *"refetch everything
 * you hold"* and the ordinary read path produces the truth. It is one wasted refetch after a
 * network blip, against a whole subsystem.
 */
exports.LIVE_FRAMES = ['change', 'resync'];
//# sourceMappingURL=contract.js.map